import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { cn } from '../../lib/utils';

const CATEGORY_LABELS = {
  all: 'Todos',
  classic: 'Clássico',
  romantic: 'Romântico',
  modern: 'Moderno',
  nature: 'Natural',
  beach: 'Praia',
  luxury: 'Luxo',
  editorial: 'Editorial',
};

// Theme-specific mini divider ornament
function MiniOrnament({ themeId, dividerStyle, accent }) {
  const s = { color: accent };
  const line = (w = 18) => (
    <span style={{ width: `${w}px`, height: '0.5px', background: accent, display: 'inline-block', opacity: 0.85 }} />
  );

  if (dividerStyle === 'floral' || ['romantic', 'floral-garden'].includes(themeId)) {
    return (
      <span style={s} className="flex items-center gap-1">
        {line(14)}<span style={{ fontSize: '11px' }}>❀</span>{line(14)}
      </span>
    );
  }
  if (dividerStyle === 'sparkles' || themeId === 'cute') {
    return <span style={{ ...s, fontSize: '10px', letterSpacing: '0.18em' }}>✦ ✧ ✦</span>;
  }
  if (dividerStyle === 'branch' || dividerStyle === 'botanical' || ['boho', 'nature', 'rustic-country', 'tropical-botanic'].includes(themeId)) {
    return (
      <span style={s} className="flex items-center gap-1">
        <span style={{ fontSize: '9px' }}>❦</span>
        {line(14)}
        <span style={{ fontSize: '9px' }}>❧</span>
      </span>
    );
  }
  if (dividerStyle === 'waves' || ['beach', 'mediterranean'].includes(themeId)) {
    return <span style={{ ...s, fontSize: '12px', letterSpacing: '0.12em' }}>◠◡◠</span>;
  }
  if (['luxury', 'starlight-night', 'urban', 'mono-editorial', 'editorial'].includes(themeId) || dividerStyle === 'editorial' || dividerStyle === 'geometry') {
    return (
      <span style={s} className="flex items-center gap-1.5">
        {line(22)}<span style={{ fontSize: '5px', opacity: 0.9 }}>◆</span>{line(22)}
      </span>
    );
  }
  // Default classic
  return (
    <span style={s} className="flex items-center gap-1">
      {line(16)}<span style={{ fontSize: '8px', opacity: 0.85 }}>✦</span>{line(16)}
    </span>
  );
}

// Per-theme heading style for the mini card
function getHeadingStyle(themeId) {
  const italic = ['romantic', 'floral-garden', 'luxury', 'vintage-classic', 'starlight-night', 'nature', 'beach', 'mediterranean', 'cute', 'classic'];
  const heavy = ['editorial', 'mono-editorial', 'urban', 'minimal'];
  const light = ['starlight-night', 'luxury'];
  const upper = ['editorial', 'mono-editorial', 'urban', 'minimal'];
  return {
    fontStyle: italic.includes(themeId) ? 'italic' : 'normal',
    fontWeight: heavy.includes(themeId) ? 800 : light.includes(themeId) ? 300 : 600,
    textTransform: upper.includes(themeId) ? 'uppercase' : 'none',
    letterSpacing: heavy.includes(themeId) ? '0.06em' : light.includes(themeId) ? '0.04em' : '0',
    fontSize: heavy.includes(themeId) ? '15px' : '20px',
  };
}

// Luminance-based dark detection (same logic as the public page)
function isDarkHex(hex) {
  let h = String(hex || '').replace('#', '').trim();
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6) return false;
  const r = parseInt(h.slice(0, 2), 16);
  const g = parseInt(h.slice(2, 4), 16);
  const b = parseInt(h.slice(4, 6), 16);
  if ([r, g, b].some(Number.isNaN)) return false;
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.42;
}

// Per-theme art motif (mirrors the public page personality)
function motifFor(id) {
  if (['romantic', 'floral-garden', 'cute'].includes(id)) return 'floral';
  if (['nature', 'tropical-botanic', 'editorial', 'boho', 'rustic-country'].includes(id)) return 'botanical';
  if (['luxury'].includes(id)) return 'deco';
  if (['starlight-night'].includes(id)) return 'celestial';
  if (['beach', 'mediterranean'].includes(id)) return 'wave';
  if (['minimal', 'mono-editorial', 'urban'].includes(id)) return 'geometric';
  return 'classic';
}

// Compact illustrated arch — a mini of the real hero art
function MiniArch({ motif, accent, primary }) {
  const s = { fill: 'none', stroke: accent, strokeWidth: 1.4, strokeLinecap: 'round' };
  const p = { fill: 'none', stroke: primary, strokeWidth: 1, strokeLinecap: 'round' };
  const rose = (x, y, r) => (
    <g transform={`translate(${x} ${y})`}>
      <circle r={r * 1.3} fill={accent} opacity="0.16" />
      <path d={`M0 ${-r} C ${r * 0.8} ${-r} ${r} ${-r * 0.2} ${r * 0.55} ${r * 0.3} C ${r} ${r * 0.7} ${r * 0.8} ${r * 1.2} 0 ${r * 1.1} C ${-r * 0.8} ${r * 1.2} ${-r} ${r * 0.7} ${-r * 0.55} ${r * 0.3} C ${-r} ${-r * 0.2} ${-r * 0.8} ${-r} 0 ${-r} Z`} {...s} />
    </g>
  );
  const leaf = (x, y, rot) => <path d="M0 0 C 7 -3 12 -10 13 -19 C 5 -16 1 -9 0 0 Z" {...s} transform={`translate(${x} ${y}) rotate(${rot})`} />;
  const star = (x, y, r) => <path d={`M${x} ${y - r} L${x + r * 0.28} ${y - r * 0.28} L${x + r} ${y} L${x + r * 0.28} ${y + r * 0.28} L${x} ${y + r} L${x - r * 0.28} ${y + r * 0.28} L${x - r} ${y} L${x - r * 0.28} ${y - r * 0.28} Z`} fill={accent} opacity="0.85" />;
  return (
    <svg viewBox="0 0 200 150" className="absolute inset-0 w-full h-full" preserveAspectRatio="xMidYMid meet" aria-hidden>
      <path d="M40 145 C 30 80 70 30 100 26 C 130 30 170 80 160 145" {...s} opacity="0.4" />
      <path d="M52 145 C 44 90 76 45 100 42 C 124 45 156 90 148 145" {...p} opacity="0.22" />
      {motif === 'floral' && (<g>{rose(100, 30, 7)}{rose(80, 40, 5)}{rose(120, 40, 5)}{leaf(100, 20, 0)}{leaf(60, 70, -50)}{leaf(140, 70, 50)}{rose(52, 92, 5)}{rose(148, 92, 5)}</g>)}
      {motif === 'botanical' && (<g>{leaf(100, 46, 0)}{leaf(92, 40, -28)}{leaf(108, 40, 28)}{leaf(60, 78, -54)}{leaf(140, 78, 54)}{leaf(56, 116, -76)}{leaf(144, 116, 76)}</g>)}
      {motif === 'deco' && (<g><path d="M100 22 L116 40 L100 58 L84 40 Z" {...s} />{star(60, 84, 6)}{star(140, 84, 6)}{star(70, 120, 4)}{star(130, 120, 4)}<path d="M70 50 L70 36 M58 50 L82 50" {...s} strokeWidth="1" /><path d="M130 50 L130 36 M118 50 L142 50" {...s} strokeWidth="1" /></g>)}
      {motif === 'celestial' && (<g><circle cx="100" cy="28" r="9" {...s} opacity="0.6" />{star(100, 28, 7)}{star(60, 64, 6)}{star(140, 64, 6)}{star(54, 110, 4)}{star(146, 110, 4)}{star(82, 44, 3)}{star(118, 44, 3)}</g>)}
      {motif === 'wave' && (<g>{[44, 58, 72].map((y, i) => (<path key={i} d={`M44 ${y} Q 64 ${y - 12} 84 ${y} T 124 ${y} T 160 ${y}`} {...s} opacity={0.7 - i * 0.2} />))}{leaf(58, 128, -16)}{leaf(142, 128, 16)}</g>)}
      {motif === 'geometric' && (<g><rect x="90" y="22" width="20" height="20" {...s} transform="rotate(45 100 32)" /><line x1="58" y1="55" x2="58" y2="132" {...s} opacity="0.4" /><line x1="142" y1="55" x2="142" y2="132" {...s} opacity="0.4" /><circle cx="58" cy="92" r="3" fill={accent} /><circle cx="142" cy="92" r="3" fill={accent} /></g>)}
      {motif === 'classic' && (<g>{rose(100, 30, 7)}{rose(78, 42, 5)}{rose(122, 42, 5)}{leaf(100, 20, 0)}{leaf(58, 96, -64)}{leaf(142, 96, 64)}{star(64, 60, 4)}{star(136, 60, 4)}</g>)}
    </svg>
  );
}


// Per-theme section strip visual
function getMiniSectionStyle(themeId, accent, primary, dark) {
  const editorial = ['editorial', 'mono-editorial', 'urban', 'minimal', 'luxury', 'clean-white'];
  return {
    borderRadius: editorial.includes(themeId) ? '2px' : '999px',
    background: dark ? 'rgba(255,255,255,0.07)' : `${accent}12`,
    border: `0.5px solid ${dark ? 'rgba(255,255,255,0.14)' : `${accent}2e`}`,
    color: dark ? 'rgba(255,255,255,0.55)' : primary,
  };
}

export function ThemeCategoryTabs({ value, onChange }) {
  const tabs = [
    { id: 'all', label: 'Todos' },
    { id: 'classic', label: 'Clássico' },
    { id: 'romantic', label: 'Romântico' },
    { id: 'modern', label: 'Moderno' },
    { id: 'nature', label: 'Natural' },
    { id: 'beach', label: 'Praia' },
    { id: 'luxury', label: 'Luxo' },
    { id: 'editorial', label: 'Editorial' },
  ];
  return (
    <div className="flex flex-wrap gap-2">
      {tabs.map((tab) => (
        <motion.button
          key={tab.id}
          type="button"
          onClick={() => onChange(tab.id)}
          whileHover={{ y: -1 }}
          whileTap={{ scale: 0.97 }}
          className={cn(
            'px-4 py-1.5 rounded-full text-xs font-semibold tracking-wide transition-all duration-200',
            value === tab.id
              ? 'bg-gray-900 text-white shadow-sm'
              : 'bg-white text-gray-500 border border-gray-200 hover:border-gray-300 hover:text-gray-800'
          )}
        >
          {tab.label}
        </motion.button>
      ))}
    </div>
  );
}

export function TemplatePreviewCard({ theme, selected, onSelect, index = 0 }) {
  const headingFont = theme.fonts?.heading || theme.font?.heading;
  const accent = theme.colors?.accent;
  const primary = theme.colors?.primary;
  const bg = theme.colors?.background || theme.colors?.bg || '#ffffff';
  // Temas com hero.image renderizam texto sobre a imagem. Cada tema define
  // theme.hero.textColor (default '#fff') — temas com fundo claro (cerejeira,
  // peonia, eucalipto, sumi-e) usam texto escuro pra ter contraste.
  const hasHeroImage = !!theme.hero?.image;
  const heroTextColor = theme.hero?.textColor || '#ffffff';
  const heroTextIsLight = !isDarkHex(heroTextColor);
  // O fundo das tabs/footer ainda segue a logica antiga (bg do tema), nao
  // o overlay do hero.image — entao mantemos o "dark" baseado no bg.
  const dark = isDarkHex(bg);
  const ink = hasHeroImage ? heroTextColor : (dark ? '#f3ede4' : primary);
  // Cores secundarias adaptativas pra textos sobre hero.image (eyebrow e data).
  const heroSubtleColor = hasHeroImage
    ? (heroTextIsLight ? 'rgba(255,255,255,0.55)' : `${heroTextColor}99`)
    : (dark ? 'rgba(255,255,255,0.55)' : `${primary}88`);
  const heroFaintColor = hasHeroImage
    ? (heroTextIsLight ? 'rgba(255,255,255,0.6)' : `${heroTextColor}aa`)
    : (dark ? 'rgba(255,255,255,0.6)' : `${primary}aa`);
  const heroAccentForArt = hasHeroImage ? heroTextColor : accent;
  const motif = motifFor(theme.id);

  const headingStyle = getHeadingStyle(theme.id);

  return (
    <motion.button
      type="button"
      onClick={() => onSelect(theme.id)}
      initial={{ opacity: 0, y: 24 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.04, duration: 0.45, ease: [0.23, 1, 0.32, 1] }}
      whileHover={{ y: selected ? 0 : -6, scale: selected ? 1 : 1, transition: { duration: 0.25, ease: [0.23, 1, 0.32, 1] } }}
      whileTap={{ scale: 0.985 }}
      className="group relative overflow-hidden text-left w-full"
      style={{
        borderRadius: selected ? '20px' : '16px',
        outline: selected ? `3px solid ${accent}` : '3px solid transparent',
        outlineOffset: selected ? '3px' : '0px',
        boxShadow: selected
          ? `0 0 0 0px ${accent}, 0 24px 64px ${accent}45, 0 12px 32px rgba(0,0,0,0.20)`
          : '0 4px 24px rgba(0,0,0,0.09)',
        transform: selected ? 'scale(1.02)' : 'scale(1)',
        transition: 'box-shadow 0.35s ease, transform 0.35s cubic-bezier(0.23,1,0.32,1), outline 0.25s ease, border-radius 0.25s ease',
        zIndex: selected ? 2 : 1,
      }}
    >
      {/* ── MINI HERO ── */}
      {/* Tema "nova geracao": background e' uma foto/arte em alta resolucao
          com overlay degrade. Tema legado: render SVG ornamental original. */}
      <div
        className="relative overflow-hidden"
        style={{
          height: '210px',
          background: theme.hero?.image
            ? `${theme.hero.overlay || 'linear-gradient(180deg, rgba(0,0,0,0.10), rgba(0,0,0,0.55))'}, url(${theme.hero.image}) center/cover no-repeat`
            : dark
              ? `radial-gradient(ellipse at 50% 30%, ${primary}66, ${bg} 72%), ${bg}`
              : `radial-gradient(ellipse at 50% 24%, ${accent}1f, transparent 56%), radial-gradient(ellipse at 82% 88%, ${primary}12, transparent 52%), ${bg}`,
        }}
      >
        {/* paper grain — só em temas legados, hero.image já tem detalhe próprio */}
        {!theme.hero?.image && (
          <div
            className="absolute inset-0 pointer-events-none"
            style={{
              backgroundImage: `radial-gradient(circle at 1px 1px, ${dark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.045)'} 1px, transparent 0)`,
              backgroundSize: '12px 12px',
            }}
          />
        )}

        {/* illustrated arch — só em temas legados */}
        {!theme.hero?.image && <MiniArch motif={motif} accent={accent} primary={primary} />}

        {/* center content */}
        <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center">
          {/* monogram */}
          <div
            className="flex items-center justify-center mb-1.5"
            style={{ width: 26, height: 26, borderRadius: '999px', border: `1px solid ${heroAccentForArt}`, color: heroAccentForArt }}
          >
            <span style={{ fontFamily: headingFont, fontSize: '9px' }}>A&P</span>
          </div>

          <span
            style={{
              color: heroSubtleColor,
              fontSize: '6.5px',
              letterSpacing: '0.26em',
              textTransform: 'uppercase',
              fontFamily: 'Inter, sans-serif',
              marginBottom: '3px',
            }}
          >
            Vamos nos casar
          </span>

          <h4
            style={{
              fontFamily: headingFont,
              color: ink,
              fontSize: headingStyle.fontSize,
              fontStyle: headingStyle.fontStyle,
              fontWeight: headingStyle.fontWeight,
              textTransform: headingStyle.textTransform,
              letterSpacing: headingStyle.letterSpacing,
              lineHeight: 1.05,
              margin: 0,
            }}
          >
            Ana
            <span style={{ display: 'block', fontFamily: "'Dancing Script', cursive", color: heroAccentForArt, fontSize: '13px', fontStyle: 'normal', fontWeight: 400, lineHeight: 1, margin: '0' }}>&amp;</span>
            Pedro
          </h4>

          <p
            style={{
              color: heroFaintColor,
              fontSize: '7.5px',
              letterSpacing: '0.18em',
              fontFamily: 'Inter, sans-serif',
              textTransform: 'uppercase',
              marginTop: '5px',
            }}
          >
            20 · Dez · 2026
          </p>

          <div className="flex items-center justify-center mt-2">
            <MiniOrnament themeId={theme.id} dividerStyle={theme.dividerStyle} accent={heroAccentForArt} />
          </div>
        </div>

        {/* Selected state overlay ribbon */}
        {selected && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="absolute inset-0 pointer-events-none"
            style={{ background: `linear-gradient(135deg, ${accent}22 0%, transparent 50%)` }}
          />
        )}

        {/* Selected check badge */}
        <motion.div
          initial={{ scale: 0.5, opacity: 0 }}
          animate={{ scale: selected ? 1 : 0.5, opacity: selected ? 1 : 0 }}
          transition={{ type: 'spring', stiffness: 400, damping: 20 }}
          className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center shadow-lg"
          style={{ background: accent, boxShadow: `0 3px 14px ${accent}88` }}
        >
          <Check size={13} color="white" strokeWidth={3} />
        </motion.div>

        {/* "Selecionado" floating pill at bottom of hero */}
        {selected && (
          <motion.div
            initial={{ y: 8, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.05, duration: 0.3 }}
            className="absolute bottom-2 left-1/2 -translate-x-1/2"
            style={{
              background: accent,
              color: '#fff',
              fontSize: '9px',
              fontWeight: 700,
              padding: '3px 12px',
              borderRadius: '999px',
              letterSpacing: '0.10em',
              textTransform: 'uppercase',
              boxShadow: `0 4px 16px ${accent}66`,
              whiteSpace: 'nowrap',
            }}
          >
            ✓ Selecionado
          </motion.div>
        )}
      </div>

      {/* ── MINI SECTION TABS ─────────────────────────────── */}
      <div
        className="flex items-center gap-1 px-3 py-2"
        style={{ background: dark ? '#0d0d0d' : `${accent}09` }}
      >
        {['Presentes', 'RSVP', 'Fotos'].map((label) => {
          const sStyle = getMiniSectionStyle(theme.id, accent, primary, dark);
          return (
            <div
              key={label}
              className="flex-1 flex items-center justify-center"
              style={{
                height: '20px',
                fontSize: '7px',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 500,
                letterSpacing: '0.04em',
                ...sStyle,
              }}
            >
              {label}
            </div>
          );
        })}
      </div>

      {/* ── THEME INFO ────────────────────────────────────── */}
      <div
        className="px-4 py-3.5 transition-all duration-300"
        style={{ background: selected ? (dark ? '#1a1010' : `${accent}08`) : (dark ? '#111111' : '#ffffff') }}
      >
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0 flex-1">
            <p
              className="font-bold truncate"
              style={{
                fontSize: '13px',
                color: dark ? '#f3f3f3' : '#111',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {theme.name}
            </p>
            <p
              className="truncate mt-0.5"
              style={{
                fontSize: '11px',
                color: dark ? 'rgba(255,255,255,0.40)' : 'rgba(0,0,0,0.40)',
                fontFamily: 'Inter, sans-serif',
              }}
            >
              {theme.archetype}
            </p>
          </div>
          <span
            className="shrink-0 text-[9px] font-bold uppercase tracking-wide rounded-full whitespace-nowrap"
            style={{
              padding: '2px 8px',
              background: `${accent}18`,
              color: accent,
              border: `0.5px solid ${accent}35`,
              letterSpacing: '0.08em',
            }}
          >
            {CATEGORY_LABELS[theme.category] || theme.category}
          </span>
        </div>
      </div>
    </motion.button>
  );
}
